// src/utils/api.js

import axios from "axios";
import { BlobServiceClient } from "@azure/storage-blob";

// URLs for the API endpoints
const SELL_ARTWORK_URL = process.env.REACT_APP_SELL_ARTWORK;
const SAS_TOKEN_URL = process.env.REACT_APP_GENERATE_SAS_TOKEN;
const UPDATE_ARTWORK_URL = process.env.REACT_APP_UPDATE_ARTWORK;
export const BLOB_URI = process.env.REACT_APP_BLOB_URI;
export const CONTAINER_NAME = process.env.REACT_APP_CONTAINER_NAME;

// Utility function to generate a random number
const generateRandomNumber = () => {
  return Math.floor(Math.random() * 10000000); // Generates a random number between 0 and 9,999,999
};

// Utility function to retrieve the bearer token from localStorage
const getAuthToken = () => {
  const authResponseData = localStorage.getItem("authResponseData");
  if (authResponseData) {
    const { token } = JSON.parse(authResponseData);
    return token;
  }
  throw new Error("Please sign in.");
};

// Function to convert Uint8Array to base64 string
const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary); // Encode the binary data into base64
};

// API call to generate submission IDs
export const generateSubmissionIds = async () => {
  try {
    const token = getAuthToken(); // Get the token from localStorage
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    // Generate a title with a random number if not provided
    const generatedTitle = `<No_Title>_${generateRandomNumber()}`;

    console.log("generatedTitle: ", generatedTitle);

    // Send a POST request to generate the artwork ID with the required body
    const response = await axios.post(
      SELL_ARTWORK_URL,
      {
        CampaignID: "",
        AllowPurchase: true,
        Art: {
          Id: null,
          ArtworkId: null,
          Title: generatedTitle,
          Artist: "",
          ArtistFirstName: "",
          ArtistLastName: "",
          Year: "",
          Description: "",
          Condition: "",
          Dimension: "",
          Medium: "",
          Resolution: "",
          MarkAsMature: false,
          AllowWallpaper: true,
          ArtistBiography: "",
          ArtistWebURL: "",
          Tags: "",
          uploadedMediaType: "",
          expectedMediaType: "",
          swayerConvertSuccess: false,
          systemGeneratedTitle: true,
          Location: {
            PlaceholderUrl: "",
            OriginalUrl: "",
            CdnUrl: "",
          },
          Theme: {
            BorderColor: "",
          },
          EventId: null,
          Hash: "",
          IsFeatured: false,
          AllowPurchase: true,
          IsKbeanSelect: false,
          categories: [],
        },
        Sale: {
          Id: null,
          ArtworkId: null,
          SellerId: "",
          IsPrimarySale: true,
          SellAsOne: false,
          SaleComplete: false,
          IsDraft: true,
          IsPause: false,
          Shares: {
            PricePerShare: 5,
            InitialValue: 0,
            TotalShares: 1,
          },
        },
      },
      { headers }
    );

    if (response.data && response.data.art && response.data.art.artworkId) {
      return {
        submissionSaleId: response.data.art.artworkId,
        saleId: response.data.sale.id,
      };
    } else {
      throw new Error("Failed to retrieve submission ID");
    }
  } catch (error) {
    console.error("Error generating submission ID:", error);
    throw error;
  }
};

// Generate a SAS token for Azure Blob Storage
export const generateSasToken = async (submissionSaleId) => {
  try {
    const token = getAuthToken(); // Get the token from localStorage
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    // Body to request SAS token for specific submissionId
    const body = {
      ArtworkID: submissionSaleId,
      ContainerName: CONTAINER_NAME,
    };

    // Send a POST request to generate the SAS token
    const response = await axios.post(SAS_TOKEN_URL, body, { headers });

    // Log the full response to inspect its structure
    console.log("SAS token API response:", response);

    // Check if the response is a string directly
    if (typeof response.data === "string") {
      console.log("SAS token retrieved from API:", response.data);
      return response.data; // Return the SAS token directly
    } else {
      console.error(
        "Unexpected response format. SAS token not found:",
        response.data
      );
      throw new Error("Failed to retrieve SAS token from the API.");
    }
  } catch (error) {
    console.error("API SAS token generation failed:", error);
    throw new Error("Failed to generate SAS token.");
  }
};

// Function to generate submission IDs and then the SAS token
export const createSubmissionAndGenerateSasToken = async () => {
  try {
    // Step 1: Generate submission IDs
    const { submissionSaleId, saleId } = await generateSubmissionIds();

    // Step 2: Generate SAS token using the submissionSaleId
    const sasToken = await generateSasToken(submissionSaleId);

    // Return both the SAS token and the IDs
    return {
      submissionSaleId,
      saleId,
      sasToken,
    };
  } catch (error) {
    console.error("Error during submission and SAS token generation:", error);
    throw error;
  }
};

// Helper function to split the file into chunks
const splitFileIntoChunks = (file, chunkSize) => {
  const chunks = [];
  let offset = 0;
  while (offset < file.size) {
    const chunk = file.slice(offset, offset + chunkSize);
    chunks.push(chunk);
    offset += chunkSize;
  }
  return chunks;
};

// Helper function for retry logic
const stageBlockWithRetry = async (
  blockBlobClient,
  blockId,
  chunk,
  size,
  retries = 3
) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await blockBlobClient.stageBlock(blockId, chunk, size);
    } catch (error) {
      console.warn(`Attempt ${attempt} failed for block ID ${blockId}:`, error);
      if (attempt === retries) throw error;
      // Exponential backoff
      await new Promise((res) => setTimeout(res, Math.pow(2, attempt) * 1000));
    }
  }
};

// Upload a file to Azure Blob Storage manually handling chunks
export const uploadFileToAzure = async (
  file,
  submissionSaleId,
  sasToken,
  onProgress
) => {
  try {
    console.log(
      `Starting manual upload for file: ${file.name}, size: ${file.size} bytes`
    );

    const blobServiceClient = new BlobServiceClient(`${BLOB_URI}?${sasToken}`);
    const containerClient =
      blobServiceClient.getContainerClient(CONTAINER_NAME);
    const blockBlobClient = containerClient.getBlockBlobClient(
      `${submissionSaleId}`
    );

    const contentType = file.type || "application/octet-stream";

    const blockSize = 4 * 1024 * 1024; // 4MB
    const chunks = splitFileIntoChunks(file, blockSize);

    const blockIds = [];

    for (let i = 0; i < chunks.length; i++) {
      // Generate a zero-padded block ID
      const blockId = btoa(`block-${i.toString().padStart(6, "0")}`);
      blockIds.push(blockId);

      console.log(`Uploading block ${i + 1}/${chunks.length}`);
      console.log(`Block ID: ${blockId}`);
      console.log(`Chunk Size: ${chunks[i].size} bytes`);

      const uploadResponse = await stageBlockWithRetry(
        blockBlobClient,
        blockId,
        chunks[i],
        chunks[i].size
      );
      console.log(
        `Block ${i + 1} uploaded with request ID: ${uploadResponse.requestId}`
      );

      // Update progress
      if (onProgress) {
        const loadedBytes = Math.min((i + 1) * blockSize, file.size);
        onProgress({
          loadedBytes,
          totalBytes: file.size,
        });
      }
    }

    // Commit the blocks
    const commitResponse = await blockBlobClient.commitBlockList(blockIds, {
      blobHTTPHeaders: {
        blobContentType: contentType,
      },
    });

    console.log(
      `File "${file.name}" uploaded successfully with request ID: ${commitResponse.requestId} and content type: ${contentType}`
    );

    // Return commitResponse along with contentType
    return {
      commitResponse,
      contentType,
    };
  } catch (error) {
    console.error("Error uploading file manually to Azure:", error);
    throw error;
  }
};

// Function to update artwork metadata after the successful file upload
export const updateArtwork = async (
  submissionSaleId,
  fileUrl,
  fileName,
  contentMD5
) => {
  try {
    const token = getAuthToken(); // Get the token from localStorage
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    // Convert contentMD5 from Uint8Array to base64 string
    const md5HashBase64 = arrayBufferToBase64(contentMD5);

    // Request body
    const body = {
      URL: fileUrl, // Blob URL
      ArtworkId: submissionSaleId, // Artwork ID
      FileName: fileName,
      MD5Hash: md5HashBase64, // Base64-encoded MD5 hash
    };

    // Send the POST request to update artwork
    const response = await axios.post(UPDATE_ARTWORK_URL, body, { headers });

    if (response.status === 200) {
      console.log("Artwork successfully updated!");
    } else {
      throw new Error("Failed to update artwork metadata.");
    }

    return response.data;
  } catch (error) {
    console.error("Error updating artwork:", error);
    throw error;
  }
};

// Final step: Update artwork metadata after file upload
export const updateArtworkMetadata = async (fields, submissionData) => {
  // console.log("updateArtworkMetadata fields data: API: ", fields);
  // console.log("updateArtworkMetadata submissionData data: API: ", submissionData);

  try {
    const token = getAuthToken(); // Get the token from localStorage
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    // Prepare the request body based on user input and previously received data

    // Extract displayName safely
    const displayName = submissionData.profileData.displayName || "";

    // Split the displayName into parts
    const nameParts = displayName.trim().split(" ");

    // Assign firstName and lastName
    const firstName = nameParts[0] || "";
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

    // Ensure tags is an array
    const tagsArray = Array.isArray(fields.tags) ? fields.tags : [];

    // Extract the 'text' from each tag and join them with commas
    const tags = tagsArray.map((tag) => tag.text).join(",");

    // Get the current year
    const currentYear = new Date().getFullYear();

    // Determine the Medium based on contentType
    const medium = submissionData.contentType.startsWith("video/")
      ? "Film"
      : submissionData.contentType.startsWith("image/")
      ? "Painting"
      : "";

    const body = {
      CampaignID: "",
      AllowPurchase: true,
      Art: {
        Id: submissionData.submissionSaleId,
        ArtworkId: submissionData.submissionSaleId,
        Title: fields.title,
        Artist: "",
        ArtistFirstName: firstName,
        ArtistLastName: lastName,
        Year: currentYear.toString(), // Dynamically set to current year
        Description: fields.description,
        Condition: "",
        Dimension: "",
        Medium: medium, // Dynamically set based on contentType
        Resolution: "",
        MarkAsMature: false,
        AllowWallpaper: true,
        ArtistBiography: "",
        ArtistWebURL: "",
        Tags: tags,
        uploadedMediaType: submissionData.contentType, // Add contentType
        expectedMediaType: submissionData.contentType, // Add contentType
        swayerConvertSuccess: false,
        systemGeneratedTitle: false,
        Location: {
          PlaceholderUrl: "",
          OriginalUrl: "",
          CdnUrl: "",
        },
        Theme: {
          BorderColor: "",
        },
        EventId: null,
        Hash: "",
        IsFeatured: false,
        AllowPurchase: false,
        IsKbeanSelect: false,
        categories: [],
      },
      Sale: {
        Id: submissionData.saleId,
        ArtworkId: submissionData.submissionSaleId,
        SellerId: "",
        IsPrimarySale: true,
        SellAsOne: false,
        SaleComplete: false,
        IsDraft: false,
        IsPause: false,
        UploadedByArtist: true,
        Shares: {
          PricePerShare: submissionData.digitalSupply ? fields.fundingGoal : 5, // Use fields.fundingGoal if digitalSupply exists, otherwise 5
          InitialValue: 0,
          TotalShares: submissionData.digitalSupply
            ? submissionData.digitalSupply
            : fields.fundingGoal / 5, // Use digitalSupply if it exists, otherwise fundingGoal / 5
        },
      },
    };

    console.log("body: ", body);

    // Send the updated metadata to the API
    const response = await axios.post(SELL_ARTWORK_URL, body, { headers });

    // Return the updated artwork and sale data
    return response.data;
  } catch (error) {
    console.error("Error updating artwork metadata:", error);
    throw error;
  }
};
